import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginUser, getCurrentUser } from '../services/authService';
import { resetSessionExpiredFlag } from '../services/api';

const AuthContext = createContext();

const TOKEN_KEY = 'authToken';
const TOKEN_TYPE_KEY = 'tokenType';
const ROLE_KEY = 'ee_assistant_role';
const EMAIL_KEY = 'ee_assistant_email';
const PROFILE_KEY = 'ee_assistant_profile';
const USER_DATA_KEY = 'ee_assistant_user_data';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userData, setUserData] = useState(null);
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(true); // start true — checking token
  // Session-expired surface. Set to true when any API call returns 401
  // (or when the JWT exp claim has passed). The SessionExpiredModal
  // listens to this and overlays the app, preserving in-progress work.
  const [sessionExpired, setSessionExpired] = useState(false);

  // Validate existing token on mount.
  //
  // Optimistic bootstrap: if we have cached user data + token, render the app
  // immediately using cached data and revalidate /auth/me in the background.
  // This prevents the UI from hanging on a blank spinner if /auth/me is slow
  // or temporarily unreachable.
  useEffect(() => {
    // ── Entra SSO callback handler ──────────────────────────────────── 
    // The backend redirects here with `#sso_token=<jwt>&next=<path>` on
    // success, or `#sso_error=<code>` on failure. Pluck the token from
    // the URL fragment, store it, then strip the fragment so the user
    // never sees it in the address bar.
    let ssoTokenFromHash = null;
    let ssoNextPath = null;
    let ssoErrorCode = null;
    try {
      const hash = window.location.hash || '';
      if (hash.length > 1) {
        const params = new URLSearchParams(hash.slice(1));
        ssoTokenFromHash = params.get('sso_token');
        // Restrict next-path to same-origin relative paths to prevent open-redirect and XSS.
        const rawNext = params.get('next');
        ssoNextPath = (rawNext && /^\/(?!\/)[^\s]*$/.test(rawNext)) ? rawNext : null;
        // Sanitize error code — allow only alphanumeric + underscore/hyphen (no CRLF, no script).
        const rawError = params.get('sso_error');
        ssoErrorCode = rawError ? rawError.replace(/[^a-zA-Z0-9_-]/g, '') : null;
        if (ssoTokenFromHash || ssoErrorCode) {
          // Clean the URL — replace state without triggering a reload.
          window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search,
          );
        }
      }
    } catch { /* ignore malformed hash */ }

    if (ssoErrorCode) {
      setAuthError(`SSO_ERROR:${ssoErrorCode}`);
    }

    if (ssoTokenFromHash) {
      // Persist the token immediately so the rest of the bootstrap
      // treats it as a normal authenticated session.
      localStorage.setItem(TOKEN_KEY, ssoTokenFromHash);
      localStorage.setItem(TOKEN_TYPE_KEY, 'bearer');
      getCurrentUser(ssoTokenFromHash)
        .then((user) => {
          _setAuthState(ssoTokenFromHash, user, user.org_profile || null);
          // ssoNextPath is already validated as a relative same-origin path above.
          if (ssoNextPath && ssoNextPath !== '/' && ssoNextPath !== window.location.pathname) {
            try { window.history.replaceState(null, '', ssoNextPath); } catch { /* ignore */ }
          }
        })
        .catch(() => { _clearAuth(); })
        .finally(() => setIsLoading(false));
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setIsLoading(false);
      return;
    }

    // Try optimistic restore from cached user data
    let optimisticOk = false;
    try {
      const cachedUserRaw = localStorage.getItem(USER_DATA_KEY);
      if (cachedUserRaw) {
        const cachedUser = JSON.parse(cachedUserRaw);
        const cachedProfile = localStorage.getItem(PROFILE_KEY)
          ? JSON.parse(localStorage.getItem(PROFILE_KEY))
          : null;
        _setAuthState(token, cachedUser, cachedProfile);
        setIsLoading(false);
        optimisticOk = true;
      }
    } catch {
      /* fall through to network validation */
    }

    // Always revalidate against the server (background if optimistic)
    getCurrentUser(token)
      .then((user) => {
        const orgProfile = user.org_profile ||
          (localStorage.getItem(PROFILE_KEY) ? JSON.parse(localStorage.getItem(PROFILE_KEY)) : null);
        _setAuthState(token, user, orgProfile);
      })
      .catch(() => {
        _clearAuth();
      })
      .finally(() => {
        if (!optimisticOk) setIsLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const _setAuthState = (token, user, orgProfile) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_TYPE_KEY, 'bearer');

    const role = user.is_superuser ? 'admin' : (user.role || 'User').toLowerCase();
    const email = user.email || '';

    localStorage.setItem(ROLE_KEY, role);
    localStorage.setItem(EMAIL_KEY, email);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
    if (orgProfile) localStorage.setItem(PROFILE_KEY, JSON.stringify(orgProfile));

    setIsAuthenticated(true);
    setUserRole(role);
    setUserEmail(email);
    setUserData(user);
    setUserProfile(orgProfile || (localStorage.getItem(PROFILE_KEY) ? JSON.parse(localStorage.getItem(PROFILE_KEY)) : null));
    setAuthError('');
    // Defensive: any successful auth state set means the user has a
    // fresh, valid session. Clear any lingering session-expired surface
    // (e.g. from an earlier 401 against a now-resolved endpoint, or a
    // stale flag in api.js that wasn't reset). This fixes the
    // first-login false-positive where the modal pops up immediately
    // after a successful sign-in.
    setSessionExpired(false);
    try { resetSessionExpiredFlag(); } catch { /* ignore */ }
  };

  const _clearAuth = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_TYPE_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(PROFILE_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    setIsAuthenticated(false);
    setUserRole(null);
    setUserEmail(null);
    setUserProfile(null);
    setUserData(null);
  };

  /**
   * Login with username + password → JWT flow.
   */
  const login = async (username, password) => {
    setAuthError('');
    setIsLoading(true);
    try {
      const data = await loginUser(username, password);
      const token = data.access_token;

      // Fetch full user object via /auth/me
      const user = await getCurrentUser(token);
      _setAuthState(token, user, data.org_profile);
      return { success: true };
    } catch (err) {
      const detail = err.detail || err.message || 'Login failed';
      setAuthError(detail);
      return { success: false, error: detail };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(() => {
    _clearAuth();
    setAuthError('');
  }, []);

  // ── Session-expiry handling ───────────────────────────────────────
  // Three triggers feed the same `sessionExpired` flag:
  //   1. Any API call returning 401 (api.js / authService.js dispatch
  //      the 'auth:expired' window event).
  //   2. Cross-tab logout — another tab clears localStorage; the
  //      `storage` event mirrors that change here so this tab also
  //      surfaces the modal instead of silently breaking.
  //   3. The JWT `exp` claim elapsing while the tab is open — a single
  //      timer based on the decoded payload.
  //
  // The user resolves expiry through SessionExpiredModal, which calls
  // `dismissSessionExpired` on successful re-auth.
  useEffect(() => {
    const onExpired = () => {
      // Only show the modal if we previously thought we were signed in.
      // Otherwise the user is already on the login form — surfacing a
      // modal there would be confusing.
      if (localStorage.getItem(TOKEN_KEY)) {
        setSessionExpired(true);
      }
    };
    const onStorage = (e) => {
      // Token was cleared in another tab → surface the modal here too.
      if (e.key === TOKEN_KEY && !e.newValue && isAuthenticated) {
        setSessionExpired(true);
      }
      // Token was set by another tab (signed in elsewhere) → reflect it.
      if (e.key === TOKEN_KEY && e.newValue && !isAuthenticated) {
        // Force a revalidation to pull fresh user state.
        window.location.reload();
      }
    };
    window.addEventListener('auth:expired', onExpired);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('auth:expired', onExpired);
      window.removeEventListener('storage', onStorage);
    };
  }, [isAuthenticated]);

  // Proactive `exp`-claim timer. Decodes the JWT payload (no signature
  // verification — that's the server's job) and schedules a single
  // setTimeout to fire `auth:expired` the moment the token is no longer
  // valid. This catches the case where the user is reading the screen
  // for 45 minutes without making any API calls.
  useEffect(() => {
    if (!isAuthenticated) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    let expMs = 0;
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      if (payload && typeof payload.exp === 'number') expMs = payload.exp * 1000;
    } catch { /* malformed token — let API 401s catch it */ }
    if (!expMs) return;
    const delay = expMs - Date.now();
    // Guard against client/server clock skew. A freshly-issued token
    // should always have many minutes of validity left; if the local
    // clock thinks the token is already (or nearly) expired, we trust
    // the server's actual JWT validation over the local clock and let
    // a real 401 from the next API call be the canonical signal.
    // Without this guard, a forward-skewed client clock fires
    // `auth:expired` immediately after login and pops the modal
    // straight after sign-in.
    if (delay < 60_000) return;
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('auth:expired', {
        detail: { resumeUrl: window.location.pathname + window.location.search },
      }));
    }, delay);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  // Called by SessionExpiredModal on successful re-login. Clears the
  // de-dupe flag in api.js so future expiries can re-fire the event.
  const dismissSessionExpired = useCallback(() => {
    setSessionExpired(false);
    try { resetSessionExpiredFlag(); } catch { /* ignore */ }
  }, []);

  const refreshUser = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    try {
      const user = await getCurrentUser(token);
      const orgProfile = user.org_profile ||
        (localStorage.getItem(PROFILE_KEY) ? JSON.parse(localStorage.getItem(PROFILE_KEY)) : null);
      _setAuthState(token, user, orgProfile);
    } catch {
      _clearAuth();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userRole,
        userEmail,
        userProfile,
        userData,
        isAdmin: userRole === 'admin',
        isCreatorOrAbove: userRole === 'admin' || userRole === 'creator',
        login,
        logout,
        refreshUser,
        authError,
        isLoading,
        sessionExpired,
        dismissSessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
